import * as THREE from 'three';
import { VoxelDataSmall } from './VoxelCharacters';
import { SheepStyle } from './catalog/PetCatalog';

export interface SheepParts {
  frontLeftLeg: VoxelDataSmall[];
  frontRightLeg: VoxelDataSmall[];
  backLeftLeg: VoxelDataSmall[];
  backRightLeg: VoxelDataSmall[];
  body: VoxelDataSmall[];
  head: VoxelDataSmall[];
}

export function generateSheepParts(style: SheepStyle): SheepParts {
  const parts: SheepParts = {
    frontLeftLeg: [],
    frontRightLeg: [],
    backLeftLeg: [],
    backRightLeg: [],
    body: [],
    head: [],
  };

  let woolCol = 0xffffff;
  let woolHighlight = 0xfff9f2;
  let woolShadow = 0xded8cc;
  let faceCol = 0x3d3835;
  let legCol = 0x262220;

  if (style === 'suffolk') {
    woolCol = 0xf7ebd5;
    woolHighlight = 0xfff8ea;
    woolShadow = 0xded1bb;
    faceCol = 0x1a1a1a;
    legCol = 0x141414;
  } else if (style === 'caramel') {
    woolCol = 0xd9a86c;
    woolHighlight = 0xe8be8b;
    woolShadow = 0xb8884d;
    faceCol = 0x4a3222;
    legCol = 0x362316;
  } else if (style === 'pink') {
    woolCol = 0xffb6c1;
    woolHighlight = 0xffd1dc;
    woolShadow = 0xe699a8;
    faceCol = 0xf5d3c4;
    legCol = 0xd4a596;
  }

  // Helper to add voxels relative to local part centers
  const addVox = (
    part: VoxelDataSmall[],
    x0: number, x1: number,
    y0: number, y1: number,
    z0: number, z1: number,
    color: number
  ) => {
    for (let x = x0; x <= x1; x++) {
      for (let y = y0; y <= y1; y++) {
        for (let z = z0; z <= z1; z++) {
          part.push({ x, y, z, color });
        }
      }
    }
  };

  // 1. LEGS (Pivot at top of leg y: 0)
  // Leg box: 2x3x2 voxels going down from y: -3 to y: 0
  addVox(parts.frontLeftLeg, -1, 0, -3, 0, -1, 0, legCol);
  addVox(parts.frontLeftLeg, -1, 0, -3, -2, 0, 1, 0x111111); // hoof tip

  addVox(parts.frontRightLeg, -1, 0, -3, 0, -1, 0, legCol);
  addVox(parts.frontRightLeg, -1, 0, -3, -2, 0, 1, 0x111111);

  addVox(parts.backLeftLeg, -1, 0, -3, 0, -1, 0, legCol);
  addVox(parts.backLeftLeg, -1, 0, -3, -2, 0, 1, 0x111111);

  addVox(parts.backRightLeg, -1, 0, -3, 0, -1, 0, legCol);
  addVox(parts.backRightLeg, -1, 0, -3, -2, 0, 1, 0x111111);

  // 2. BODY (Pivot centered at lower-mid torso y: 0)
  // Underbelly shadow
  addVox(parts.body, -3, 2, 0, 0, -4, 3, woolShadow);
  // Main fluffy wool fleece core
  addVox(parts.body, -4, 3, 1, 5, -5, 4, woolCol);
  // Volumetric side wool puffs
  addVox(parts.body, -5, -5, 2, 4, -4, 3, woolHighlight);
  addVox(parts.body, 4, 4, 2, 4, -4, 3, woolHighlight);
  // Top cloud ridge
  addVox(parts.body, -3, 2, 6, 6, -4, 3, woolHighlight);
  addVox(parts.body, -2, 1, 7, 7, -3, 2, woolCol);
  // Tail puff
  addVox(parts.body, -1, 0, 2, 4, -6, -6, woolHighlight);
  // Woolly neck/collar rim
  addVox(parts.body, -3, 2, 1, 5, 5, 6, woolCol);
  addVox(parts.body, -2, 1, 6, 6, 5, 6, woolHighlight);

  // 3. HEAD (Pivot at neck connection base: x: 0, y: 0, z: 0)
  // Main face block extending forward
  addVox(parts.head, -2, 1, -1, 2, 0, 3, faceCol);
  // Snout/Muzzle
  addVox(parts.head, -1, 0, -1, 1, 4, 5, faceCol);
  // Nose tip
  addVox(parts.head, -1, 0, 0, 0, 6, 6, 0x111111);

  // Cute Eyes & catchlights
  parts.head.push({ x: -2, y: 1, z: 3, color: 0x111111 });
  parts.head.push({ x: 1, y: 1, z: 3, color: 0x111111 });
  parts.head.push({ x: -2, y: 2, z: 3, color: 0xffffff });
  parts.head.push({ x: 1, y: 2, z: 3, color: 0xffffff });

  // Rosy blush
  parts.head.push({ x: -2, y: -1, z: 3, color: 0xffa0b0 });
  parts.head.push({ x: 1, y: -1, z: 3, color: 0xffa0b0 });

  // Floppy ears
  addVox(parts.head, -4, -3, 0, 1, 1, 2, faceCol);
  addVox(parts.head, 2, 3, 0, 1, 1, 2, faceCol);

  // Fluffy crown wool puff on top of head
  addVox(parts.head, -2, 1, 3, 4, 0, 3, woolCol);
  addVox(parts.head, -1, 0, 5, 5, 1, 2, woolHighlight);

  return parts;
}

export class SheepRenderer {
  public group: THREE.Group;
  private meshes: THREE.InstancedMesh[] = [];

  // Articulated groups
  private frontLeftLegGroup: THREE.Group;
  private frontRightLegGroup: THREE.Group;
  private backLeftLegGroup: THREE.Group;
  private backRightLegGroup: THREE.Group;
  private bodyGroup: THREE.Group;
  private headGroup: THREE.Group;

  // Animation values
  private currentHeadRotationX = 0;
  private targetHeadRotationX = 0;

  constructor(public readonly style: SheepStyle, public readonly smallVoxelSize: number = 0.08) {
    this.group = new THREE.Group();
    const parts = generateSheepParts(style);

    this.frontLeftLegGroup = new THREE.Group();
    this.frontRightLegGroup = new THREE.Group();
    this.backLeftLegGroup = new THREE.Group();
    this.backRightLegGroup = new THREE.Group();
    this.bodyGroup = new THREE.Group();
    this.headGroup = new THREE.Group();

    // Set pivot positions
    // Legs pivot at the body underside
    this.frontLeftLegGroup.position.set(-2.5 * smallVoxelSize, 3.5 * smallVoxelSize, 2.5 * smallVoxelSize);
    this.frontRightLegGroup.position.set(1.5 * smallVoxelSize, 3.5 * smallVoxelSize, 2.5 * smallVoxelSize);
    this.backLeftLegGroup.position.set(-2.5 * smallVoxelSize, 3.5 * smallVoxelSize, -2.5 * smallVoxelSize);
    this.backRightLegGroup.position.set(1.5 * smallVoxelSize, 3.5 * smallVoxelSize, -2.5 * smallVoxelSize);

    // Body centered at y = 3.5
    this.bodyGroup.position.set(0, 3.5 * smallVoxelSize, 0);

    // Head pivot at neck base
    this.headGroup.position.set(0, 5.5 * smallVoxelSize, 3.5 * smallVoxelSize);

    // Add voxel geometries
    this.frontLeftLegGroup.add(this.buildPartMesh(parts.frontLeftLeg, smallVoxelSize));
    this.frontRightLegGroup.add(this.buildPartMesh(parts.frontRightLeg, smallVoxelSize));
    this.backLeftLegGroup.add(this.buildPartMesh(parts.backLeftLeg, smallVoxelSize));
    this.backRightLegGroup.add(this.buildPartMesh(parts.backRightLeg, smallVoxelSize));
    this.bodyGroup.add(this.buildPartMesh(parts.body, smallVoxelSize));
    this.headGroup.add(this.buildPartMesh(parts.head, smallVoxelSize));

    // Assemble skeleton
    this.group.add(this.frontLeftLegGroup);
    this.group.add(this.frontRightLegGroup);
    this.group.add(this.backLeftLegGroup);
    this.group.add(this.backRightLegGroup);
    this.group.add(this.bodyGroup);
    this.group.add(this.headGroup);
  }

  private buildPartMesh(voxels: VoxelDataSmall[], smallVoxelSize: number): THREE.Group {
    const partGroup = new THREE.Group();
    const colorMap = new Map<number, VoxelDataSmall[]>();
    voxels.forEach(v => {
      if (!colorMap.has(v.color)) colorMap.set(v.color, []);
      colorMap.get(v.color)!.push(v);
    });

    const geometry = new THREE.BoxGeometry(smallVoxelSize, smallVoxelSize, smallVoxelSize);
    const dummy = new THREE.Object3D();

    colorMap.forEach((vArr, color) => {
      const mat = new THREE.MeshLambertMaterial({ color });
      const mesh = new THREE.InstancedMesh(geometry, mat, vArr.length);
      mesh.castShadow = true;
      mesh.receiveShadow = true;

      vArr.forEach((v, i) => {
        dummy.position.set(v.x * smallVoxelSize, v.y * smallVoxelSize, v.z * smallVoxelSize);
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);
      });
      mesh.instanceMatrix.needsUpdate = true;
      this.meshes.push(mesh);
      partGroup.add(mesh);
    });

    return partGroup;
  }

  public update(time: number, delta: number, state: 'walking' | 'grazing' | 'idle') {
    const lerpSpeed = 6.0 * delta;

    if (state === 'walking') {
      this.targetHeadRotationX = 0.1; // Head up while walking
      const walkSpeed = 6.0;
      const legAngle = Math.sin(time * 0.001 * walkSpeed) * 0.35;

      // Diagonal legs pairing for quadrupeds (FL & BR together, FR & BL opposite)
      this.frontLeftLegGroup.rotation.x = legAngle;
      this.backRightLegGroup.rotation.x = legAngle;
      this.frontRightLegGroup.rotation.x = -legAngle;
      this.backLeftLegGroup.rotation.x = -legAngle;

      // Gentle walking body bob
      this.bodyGroup.position.y = (3.5 + Math.abs(Math.sin(time * 0.001 * walkSpeed * 2.0)) * 0.2) * this.smallVoxelSize;
      this.bodyGroup.rotation.z = Math.sin(time * 0.001 * walkSpeed) * 0.03;
      this.headGroup.rotation.z = -Math.sin(time * 0.001 * walkSpeed) * 0.04;

    } else if (state === 'grazing') {
      // Grazing posture: Head dips down towards the grass (~50 degrees)
      this.targetHeadRotationX = 0.95;

      // Smooth leg standing position
      this.frontLeftLegGroup.rotation.x = THREE.MathUtils.lerp(this.frontLeftLegGroup.rotation.x, 0, lerpSpeed);
      this.frontRightLegGroup.rotation.x = THREE.MathUtils.lerp(this.frontRightLegGroup.rotation.x, 0, lerpSpeed);
      this.backLeftLegGroup.rotation.x = THREE.MathUtils.lerp(this.backLeftLegGroup.rotation.x, 0, lerpSpeed);
      this.backRightLegGroup.rotation.x = THREE.MathUtils.lerp(this.backRightLegGroup.rotation.x, 0, lerpSpeed);

      this.bodyGroup.position.y = THREE.MathUtils.lerp(this.bodyGroup.position.y, 3.2 * this.smallVoxelSize, lerpSpeed);
      this.bodyGroup.rotation.x = THREE.MathUtils.lerp(this.bodyGroup.rotation.x, 0.06, lerpSpeed); // slight forward body tilt

      // Adorable grass munching / nibbling animation!
      const nibble = Math.sin(time * 0.012) * 0.08;
      const chew = Math.cos(time * 0.016) * 0.04;
      this.headGroup.rotation.z = chew;
      this.headGroup.rotation.y = nibble * 0.5;

    } else {
      // Idle standing
      this.targetHeadRotationX = 0;

      this.frontLeftLegGroup.rotation.x = THREE.MathUtils.lerp(this.frontLeftLegGroup.rotation.x, 0, lerpSpeed);
      this.frontRightLegGroup.rotation.x = THREE.MathUtils.lerp(this.frontRightLegGroup.rotation.x, 0, lerpSpeed);
      this.backLeftLegGroup.rotation.x = THREE.MathUtils.lerp(this.backLeftLegGroup.rotation.x, 0, lerpSpeed);
      this.backRightLegGroup.rotation.x = THREE.MathUtils.lerp(this.backRightLegGroup.rotation.x, 0, lerpSpeed);

      this.bodyGroup.position.y = THREE.MathUtils.lerp(this.bodyGroup.position.y, 3.5 * this.smallVoxelSize, lerpSpeed);
      this.bodyGroup.rotation.x = THREE.MathUtils.lerp(this.bodyGroup.rotation.x, 0, lerpSpeed);
      this.bodyGroup.rotation.z = THREE.MathUtils.lerp(this.bodyGroup.rotation.z, 0, lerpSpeed);

      // Subtle breathing head movement
      const breathe = Math.sin(time * 0.002) * 0.03;
      this.headGroup.rotation.z = breathe;
    }

    // Smooth head rotation interpolation (for dipping head down to eat grass or raising it back up)
    this.currentHeadRotationX = THREE.MathUtils.lerp(this.currentHeadRotationX, this.targetHeadRotationX, lerpSpeed);
    this.headGroup.rotation.x = this.currentHeadRotationX;
  }

  public dispose() {
    this.meshes.forEach(m => {
      m.geometry.dispose();
      (m.material as THREE.Material).dispose();
    });
  }
}
